import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class EqCartNotification extends Component {
  #abortController = null;
  #hideTimer = null;
  #hiddenTimer = null;
  /** @type {HTMLElement | null} */
  #suppressedDrawer = null;
  #suppressDepth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    // Horizon's drawer reads its own `auto-open` while the event dispatches on `document`, so this
    // lifts the attribute from a capture listener on `window`, which runs first, leaving the drawer
    // shut for the adds this notification is about to confirm.
    window.addEventListener(StandardEvents.cartLinesUpdate, this.#liftAutoOpen, { signal, capture: true });
    this.refs.closeButton?.addEventListener('click', this.#hide, { signal });
    this.refs.viewBasket?.addEventListener('click', this.#openCart, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#hideTimer) window.clearTimeout(this.#hideTimer);
    this.#hideTimer = null;
    if (this.#hiddenTimer) window.clearTimeout(this.#hiddenTimer);
    this.#hiddenTimer = null;
    // A re-render between the lift and its microtask would otherwise leave the drawer unable to
    // auto-open for good.
    this.#suppressDepth = 1;
    this.#restoreAutoOpen();
    super.disconnectedCallback();
  }

  #liftAutoOpen = (event) => {
    if (!this.#triggerFor(event)) return;

    const drawer = document.querySelector('cart-drawer-component');
    if (!(drawer instanceof HTMLElement)) return;

    if (this.#suppressDepth === 0) {
      if (!drawer.hasAttribute('auto-open')) return;

      drawer.removeAttribute('auto-open');
      this.#suppressedDrawer = drawer;
    }

    this.#suppressDepth++;

    // Queued here rather than restored from a second listener: dispatch is synchronous, so this
    // runs once the whole event has been delivered — including to the drawer — and it still runs
    // if a listener in between calls `stopPropagation`, or if the event never bubbles. Left to a
    // bubble listener, either case would strand `auto-open` off for the rest of the page's life.
    queueMicrotask(this.#restoreAutoOpen);
  };

  #restoreAutoOpen = () => {
    if (this.#suppressDepth === 0) return;

    this.#suppressDepth--;

    // Overlapping adds nest, so only the outermost one puts the attribute back.
    if (this.#suppressDepth > 0) return;

    this.#suppressedDrawer?.setAttribute('auto-open', '');
    this.#suppressedDrawer = null;
  };

  /**
   * The element this notification would announce, or null when the event is not one of ours.
   *
   * @param {{ target: EventTarget | null, action?: string }} event
   * @returns {HTMLElement | null}
   */
  #triggerFor(event) {
    if (!window.matchMedia('(max-width: 749px)').matches) return null;
    if (event.action !== 'add' && event.action !== 'remove') return null;
    const source = event.target instanceof Element ? event.target : null;
    const trigger = source?.closest('[data-eq-cart-mobile-notification="true"]');
    return trigger instanceof HTMLElement ? trigger : null;
  }

  #handleCartUpdate = (event) => {
    const trigger = this.#triggerFor(event);
    if (!trigger) return;

    event.promise
      ?.then(() => {
        const isRemove = event.action === 'remove';
        this.refs.status.textContent = isRemove ? this.dataset.removedText || '' : this.dataset.addedText || '';
        this.refs.status.toggleAttribute('data-remove', isRemove);
        this.refs.product.textContent = trigger.dataset.eqCartNotificationName || '';

        const image = trigger.dataset.eqCartNotificationImage || '';
        if (image) {
          this.refs.media.style.backgroundImage = `url("${image.replace(/"/g, '%22')}")`;
          this.refs.media.setAttribute('data-has-image', '');
        } else {
          this.refs.media.style.backgroundImage = '';
          this.refs.media.removeAttribute('data-has-image');
        }

        if (this.#hiddenTimer) window.clearTimeout(this.#hiddenTimer);
        this.#hiddenTimer = null;
        this.hidden = false;
        requestAnimationFrame(() => this.setAttribute('data-active', ''));
        if (this.#hideTimer) window.clearTimeout(this.#hideTimer);
        this.#hideTimer = window.setTimeout(this.#hide, 3000);
      })
      .catch(() => {});
  };

  #hide = () => {
    this.removeAttribute('data-active');
    if (this.#hiddenTimer) window.clearTimeout(this.#hiddenTimer);
    this.#hiddenTimer = window.setTimeout(() => { this.hidden = true; }, 300);
  };

  #openCart = () => {
    this.#hide();
    const drawer = document.querySelector('#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };
}

if (!customElements.get('eq-cart-notification')) {
  customElements.define('eq-cart-notification', EqCartNotification);
}
